/**
 * Progression HUD — the XP bar, the level-up callout, and the post-match summary.
 *
 * Three blocks, all top-centre, in a column that nothing else in the HUD claims:
 *
 *   .pg-bar       top centre, 14 px down — rank, thin bar, XP into level
 *   .pg-callout   directly under it — the "LEVEL 12 / UNLOCKED" moment
 *   .pg-summary   centre screen, on match end only — where the match's XP went
 *
 * The scorestreak callout lives at 21% and the killfeed at the right edge, so this
 * column is clear. The bar carries `hud-fade` so it goes away with the rest of the
 * HUD when the game pauses.
 *
 * Follows the same discipline as the base HUD: every DOM write is guarded by a
 * cached last-written value, nothing in the frame path reads layout, and the only
 * animated properties are `opacity`, `transform` and `color`. At rest the whole
 * block writes nothing at all — see `update()`, which early-outs on an idle frame.
 */

import { Progression, TUNING as MODEL } from '../game/Progression.js';
import {
  KIND_GLYPH,
  KIND_LABEL,
  LEVEL_INITIAL,
  WEAPON_MAX_LEVEL,
  emptyLevels,
  setWeaponSource,
  weaponRoster,
} from '../game/unlocks.data.js';
import { install as installDevPanel } from './DevPanel.js';
import { EquipmentScreen } from './EquipmentScreen.js';
import { IconForge } from './IconForge.js';
import { GunsmithScreen } from './Gunsmith.js';
import { LoadoutScreen } from './Loadout.js';

export const TUNING = {
  litTime: 2.4, // s the bar stays bright after XP lands
  gainLife: 1.6, // s the "+150 HEADSHOT" readout is visible
  calloutLife: 3.4, // s the level-up callout holds
  calloutQueueMax: 2, // most callouts we will ever hold back; see the `level:up` bind
  barCatchUpMax: 2, // levels the bar will animate through before it just snaps
  calloutIn: 0.18,
  calloutOut: 0.5,
  barEase: 6.5, // 1/s — how fast the fill chases its target
  barSnap: 0.0015, // fraction below which we stop animating and idle
  summaryLife: 9.0, // s the post-match summary holds before fading
};

const SOURCE_LABEL = {
  kill: 'ELIMINATIONS',
  headshot: 'HEADSHOTS',
  multikill: 'MULTI-KILLS',
  streak: 'KILL STREAKS',
  match: 'MISSION COMPLETE',
  debug: 'DEBUG',
  reset: 'RESET',
};

const GAIN_LABEL = {
  kill: 'ELIMINATION',
  headshot: 'HEADSHOT',
  multikill: 'MULTI-KILL',
  streak: 'STREAK',
  match: 'MISSION',
};

export class ProgressionHUD {
  /**
   * @param {object} ctx GameContext
   * @param {Progression} model the career-XP model; created here if not supplied
   */
  constructor(ctx, model) {
    this.ctx = ctx;
    this.model = model ?? new Progression(ctx);

    const host = document.getElementById('hud') ?? document.getElementById('ui-root') ?? document.body;
    const root = document.createElement('div');
    root.className = 'pg-root';
    host.appendChild(root);
    this.root = root;

    this._build(root);
    this._initState();
    this._bind();

    this._unregister = ctx.engine?.registerUpdate?.((dt) => this.update(dt), 'ui');
  }

  /* -------------------------------- markup -------------------------------- */

  _build(root) {
    const bar = document.createElement('div');
    bar.className = 'pg-bar hud-fade';
    bar.innerHTML = `
      <div class="pg-bar-head">
        <span class="pg-rank"><i>LV</i><b>1</b></span>
        <span class="pg-gain"></span>
        <span class="pg-nums"><b>0</b><span>/</span><i>0</i></span>
      </div>
      <div class="pg-track"><i></i></div>`;
    root.appendChild(bar);
    this.barEl = bar;
    this.rankEl = bar.querySelector('.pg-rank b');
    this.gainEl = bar.querySelector('.pg-gain');
    this.nowEl = bar.querySelector('.pg-nums b');
    this.needEl = bar.querySelector('.pg-nums i');
    this.fillEl = bar.querySelector('.pg-track i');

    // `hud-fade` on both: a rank-up banner floating over an open pause menu, or over
    // the loadout screen, reads as a stuck element. The base HUD's `is-occluded`
    // rule carries `!important`, so it wins over the inline opacity the frame loop
    // writes here — which is exactly why that rule is written that way.
    const callout = document.createElement('div');
    callout.className = 'pg-callout hud-fade';
    callout.innerHTML = `
      <div class="pg-callout-kicker">RANK UP</div>
      <div class="pg-callout-level">LEVEL 2</div>
      <div class="pg-callout-list"></div>`;
    root.appendChild(callout);
    this.calloutEl = callout;
    this.calloutLevel = callout.querySelector('.pg-callout-level');
    this.calloutList = callout.querySelector('.pg-callout-list');

    const summary = document.createElement('div');
    summary.className = 'pg-summary hud-fade';
    summary.innerHTML = `
      <div class="pg-summary-title">MATCH XP</div>
      <div class="pg-summary-rule"></div>
      <div class="pg-summary-rows"></div>
      <div class="pg-summary-total"><span>TOTAL</span><b>0</b></div>
      <div class="pg-summary-rank"></div>`;
    root.appendChild(summary);
    this.summaryEl = summary;
    this.summaryRows = summary.querySelector('.pg-summary-rows');
    this.summaryTotal = summary.querySelector('.pg-summary-total b');
    this.summaryRank = summary.querySelector('.pg-summary-rank');
  }

  _initState() {
    // The bar animates from wherever the save left it rather than from zero, so a
    // returning player does not watch their whole level fill in on boot.
    this._shownLevel = this.model.level;
    this._fill = this.model.levelFraction;
    this._targetFill = this._fill;
    this._pendingLevels = [];

    this._lit = 0;
    this._gainT = -1;
    this._calloutT = -1;
    this._summaryT = -1;
    this._idle = false;

    // Last-written DOM values. `now` / `need` hold the raw numbers, not the
    // formatted strings, so the equality check happens before any formatting.
    this._w = {
      rank: null,
      now: -1,
      nowMax: null,
      need: -1,
      needMax: null,
      fill: -1,
      lit: null,
      gain: null,
      max: null,
    };
  }

  _bind() {
    const ev = this.ctx.events;

    ev.on('xp:gain', ({ amount, reason } = {}) => {
      this._retarget();
      this._lit = TUNING.litTime;
      if (amount > 0) {
        const label = GAIN_LABEL[reason];
        this.gainEl.textContent = label ? `+${amount} ${label}` : `+${amount}`;
        this._gainT = 0;
      }
      this._idle = false;
    });

    // Queued rather than shown immediately: a single kill can cross two levels at
    // low rank and both deserve their moment. The queue drains as each callout ends.
    //
    // It is capped, and the *oldest* entries are the ones dropped. A backlog longer
    // than this can only come from a jump the player did not earn one kill at a
    // time, and in that case the level they are on now is the one worth showing —
    // playing three minutes of stale callouts while the bar sits levels behind
    // reality is worse than showing nothing.
    ev.on('level:up', (p) => {
      if (!p) return;
      this._pendingLevels.push(p);
      if (this._pendingLevels.length > TUNING.calloutQueueMax) {
        this._pendingLevels = this._pendingLevels.slice(-TUNING.calloutQueueMax);
      }
      this._idle = false;
      this.ctx.events.emit('audio:play', { id: 'ui_levelup', volume: 0.7 });
    });

    ev.on('game:state', ({ state } = {}) => {
      if (state === 'won') this._showSummary();
    });
  }

  _retarget() {
    const m = this.model;
    // More than a couple of levels behind means this was a jump, not a kill. Animating
    // through it would leave the rank number lying about the player's level for
    // several seconds, which is worse than not animating at all.
    if (Math.abs(m.level - this._shownLevel) > TUNING.barCatchUpMax) {
      this._shownLevel = m.level;
      this._fill = this._targetFill = m.levelFraction;
      return;
    }
    this._targetFill = m.levelFraction;
    // Levels the bar has not yet visually caught up to: it fills to 1, flips the
    // rank number, and keeps going. Anything else makes a double level-up look
    // like the bar simply jumped backwards.
    if (m.level > this._shownLevel) this._targetFill = 1;
    else if (m.level < this._shownLevel) {
      this._shownLevel = m.level;
      this._fill = m.levelFraction;
    }
  }

  /* --------------------------------- frame -------------------------------- */

  update(dt) {
    // Idle frames cost one boolean. The HUD is on the 'ui' phase, which still ticks
    // while paused, so this early-out is doing real work between kills.
    if (this._idle) return;

    let busy = false;
    busy = this._updateBar(dt) || busy;
    busy = this._updateGain(dt) || busy;
    busy = this._updateCallout(dt) || busy;
    busy = this._updateSummary(dt) || busy;
    this._idle = !busy;
  }

  _updateBar(dt) {
    const w = this._w;
    const m = this.model;
    let busy = false;

    // Ease toward the target; snap and stop when the difference stops mattering.
    const d = this._targetFill - this._fill;
    if (Math.abs(d) > TUNING.barSnap) {
      this._fill += d * Math.min(1, TUNING.barEase * dt);
      busy = true;
    } else {
      this._fill = this._targetFill;
      if (this._shownLevel < m.level && this._fill >= 1 - TUNING.barSnap) {
        this._shownLevel++;
        this._fill = 0;
        this._targetFill = this._shownLevel < m.level ? 1 : m.levelFraction;
        busy = true;
      }
    }

    const rank = String(this._shownLevel);
    if (rank !== w.rank) {
      this.rankEl.textContent = rank;
      w.rank = rank;
    }

    const maxed = this._shownLevel >= MODEL.maxLevel;
    if (maxed !== w.max) {
      this.barEl.classList.toggle('is-max', maxed);
      w.max = maxed;
    }

    // Numbers track the *shown* level, not the model's, so they agree with the bar
    // mid-animation instead of reading from the level you are about to be.
    //
    // The comparison is on the *number*, before formatting. `toLocaleString` costs
    // ~15 us a call — running two of them every frame of a 2.4 s highlight was on
    // its own more expensive than everything else this HUD does put together.
    const cost = maxed ? 0 : costFor(this._shownLevel);
    const into = maxed ? 0 : Math.round(this._fill * cost);
    if (into !== w.now || maxed !== w.nowMax) {
      this.nowEl.textContent = maxed ? 'MAX' : fmt(into);
      w.now = into;
      w.nowMax = maxed;
    }
    if (cost !== w.need || maxed !== w.needMax) {
      this.needEl.textContent = maxed ? 'RANK' : fmt(cost);
      w.need = cost;
      w.needMax = maxed;
    }

    const r = Math.round(this._fill * 400) / 400;
    if (r !== w.fill) {
      this.fillEl.style.transform = `scaleX(${maxed ? 1 : r})`;
      w.fill = r;
    }

    if (this._lit > 0) {
      this._lit -= dt;
      busy = true;
    }
    const lit = this._lit > 0;
    if (lit !== w.lit) {
      this.barEl.classList.toggle('is-lit', lit);
      w.lit = lit;
    }
    return busy;
  }

  _updateGain(dt) {
    if (this._gainT < 0) return false;
    this._gainT += dt;
    const t = this._gainT / TUNING.gainLife;
    if (t >= 1) {
      this._gainT = -1;
      this.gainEl.style.opacity = '0';
      return false;
    }
    const o = t < 0.12 ? t / 0.12 : 1 - Math.max(0, (t - 0.6) / 0.4);
    const s = o.toFixed(2);
    if (s !== this._w.gain) {
      this.gainEl.style.opacity = s;
      this._w.gain = s;
    }
    return true;
  }

  _updateCallout(dt) {
    if (this._calloutT < 0) {
      if (!this._pendingLevels.length) return false;
      this._openCallout(this._pendingLevels.shift());
    }
    this._calloutT += dt;
    const t = this._calloutT;
    let o = 1;
    let y = 0;
    if (t < TUNING.calloutIn) {
      const k = t / TUNING.calloutIn;
      o = k;
      y = (1 - k) * -7;
    } else if (t > TUNING.calloutLife - TUNING.calloutOut) {
      const k = (TUNING.calloutLife - t) / TUNING.calloutOut;
      o = Math.max(0, k);
      y = (1 - k) * -5;
    }
    if (t >= TUNING.calloutLife) {
      this._calloutT = -1;
      this.calloutEl.style.opacity = '0';
      return this._pendingLevels.length > 0;
    }
    this.calloutEl.style.opacity = o.toFixed(2);
    this.calloutEl.style.transform = `translate(-50%, ${y.toFixed(1)}px)`;
    return true;
  }

  _openCallout({ level, unlocked }) {
    this._calloutT = 0;
    this.calloutLevel.textContent = `LEVEL ${level}`;
    this.calloutEl.classList.toggle('is-max', level >= MODEL.maxLevel);
    // `u.name` arrives already resolved against the weapon data — see the note in
    // `unlocks.data.js` about why these entries deliberately carry no `classId`.
    // `unbuilt`, not `pending`: `Progression._decorate` overwrites `pending`.
    const rows = (unlocked ?? []).map((u) => {
      const glyph = KIND_GLYPH[u.kind] ?? '·';
      const kind = KIND_LABEL[u.kind] ?? u.kind?.toUpperCase?.() ?? '';
      const soon = u.unbuilt || u.pending ? '<em>COMING SOON</em>' : '';
      const rank = u.kind === 'weapon' && u.rank ? `<u>${esc(u.rank)}</u>` : '';
      const cls = u.kind === 'weapon' ? 'pg-unlock is-weapon' : 'pg-unlock';
      return `<div class="${cls}"><i>${glyph}</i><span>${kind}</span><b>${esc(u.name ?? '')}</b>${rank}${soon}</div>`;
    });
    this.calloutList.innerHTML = rows.join('');
  }

  /* ------------------------------- summary -------------------------------- */

  _showSummary() {
    const m = this.model.match;
    const order = ['kill', 'headshot', 'multikill', 'streak', 'match'];
    const rows = order
      .filter((k) => m.bySource[k])
      .map(
        (k) =>
          `<div class="pg-summary-row"><span>${SOURCE_LABEL[k] ?? k}</span><b>+${fmt(m.bySource[k])}</b></div>`
      );
    if (!rows.length) rows.push('<div class="pg-summary-row"><span>NO XP EARNED</span><b>0</b></div>');
    this.summaryRows.innerHTML = rows.join('');
    this.summaryTotal.textContent = `+${fmt(m.xp)}`;

    const s = this.model.snapshot();
    const gained = m.levels.length;
    this.summaryRank.innerHTML = s.maxed
      ? `<span>RANK</span><b>MAX — LEVEL ${MODEL.maxLevel}</b>`
      : `<span>RANK</span><b>LEVEL ${s.level}</b><em>${fmt(s.xpIntoLevel)} / ${fmt(s.xpForNext)}</em>` +
        (gained ? `<u>+${gained} LEVEL${gained > 1 ? 'S' : ''}</u>` : '');

    this._summaryT = 0;
    this._idle = false;
  }

  _updateSummary(dt) {
    if (this._summaryT < 0) return false;
    this._summaryT += dt;
    const t = this._summaryT;
    let o = 1;
    if (t < 0.25) o = t / 0.25;
    else if (t > TUNING.summaryLife - 0.8) o = Math.max(0, (TUNING.summaryLife - t) / 0.8);
    if (t >= TUNING.summaryLife) {
      this._summaryT = -1;
      this.summaryEl.style.opacity = '0';
      this.summaryEl.classList.remove('is-on');
      return false;
    }
    this.summaryEl.classList.add('is-on');
    this.summaryEl.style.opacity = o.toFixed(2);
    return true;
  }

  dispose() {
    this._unregister?.();
    this.root.remove();
  }
}

/* ---------------------------------------------------------------------------- */

function costFor(level) {
  // Local copy of the curve lookup so this file does not need the model instance
  // just to label the bar mid-animation.
  return xpCost(level);
}

let _costCache = null;
function xpCost(level) {
  if (!_costCache) {
    _costCache = [0];
    let prev = 0;
    for (let L = 1; L < MODEL.maxLevel; L++) {
      let c =
        Math.round((MODEL.curveBase + MODEL.curveScale * Math.pow(L, MODEL.curvePower)) / MODEL.curveQuantum) *
        MODEL.curveQuantum;
      if (c <= prev) c = prev + MODEL.curveQuantum;
      _costCache[L] = c;
      prev = c;
    }
    _costCache[MODEL.maxLevel] = 0;
  }
  return _costCache[Math.max(1, Math.min(MODEL.maxLevel, level))] ?? 0;
}

const fmt = (n) => Number(n || 0).toLocaleString('en-US');

function esc(s) {
  return String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);
}

/* ---------------------------------------------------------------------------- *
 * Bootstrap
 *
 * Progression is three objects that only make sense together: the model, this HUD,
 * and the loadout screen. Nothing in `main.js` is mine to edit, so the whole feature
 * is assembled here behind one idempotent call — the lead wires it with:
 *
 *     import { bootstrapProgression } from './ui/Progression.js';
 *     ctx.progression = bootstrapProgression(ctx);       // after ctx.hud exists
 *
 * Calling it twice returns the same instance rather than building a second HUD.
 * ---------------------------------------------------------------------------- */

export function bootstrapProgression(ctx) {
  if (ctx.__progression) return ctx.__progression;

  const model = new Progression(ctx);
  // Before the screens, because they ask it for portraits as they build their rows.
  ctx.icons = new IconForge(ctx);
  const hud = new ProgressionHUD(ctx, model);
  const loadout = new LoadoutScreen(ctx, model);
  // Constructed after the loadout screen and pointed back at it. Order matters: both
  // bind `keydown` on the capture phase and the loadout screen swallows every key
  // while it is open, so it must be the one that dispatches into the Gunsmith rather
  // than the Gunsmith listening for its own key underneath.
  const gunsmith = new GunsmithScreen(ctx, model, loadout);
  loadout.gunsmith = gunsmith;

  // Same ordering rule as the Gunsmith: after the loadout screen, dispatched into by
  // it. It also republishes the saved equipment choice on construction, so the
  // simulation starts from what the player picked rather than from its own defaults.
  const equipmentScreen = new EquipmentScreen(ctx);
  ctx.equipmentScreen = equipmentScreen;

  // Development shortcuts. Absent unless the URL carries `?dev=1`; deleting this
  // line and `DevPanel.js` removes every trace. See that file's header.
  installDevPanel(ctx);

  const api = { model, hud, loadout, gunsmith, equipmentScreen };
  ctx.__progression = api;
  ctx.progression = model;

  // Debug harness. Every reachable state without grinding for it.
  window.__progtest = {
    addXp: (n = 1000, reason = 'debug') => (model.addXp(n, reason), model.snapshot()),
    setLevel: (n = 25) => model.setLevel(n),
    unlockAll: (on = true) => model.unlockAll(on),
    reset: () => model.reset(),
    dump: () => {
      const s = model.snapshot();
      console.table(
        model.weapons.map((w) => ({
          id: w.id,
          class: w.classId,
          name: w.name,
          rank: weaponRoster().find((r) => r.id === w.id)?.rank ?? '—',
          unlock: w.level === LEVEL_INITIAL ? 'initial' : w.level,
          unlocked: model.isUnlocked(w.id),
          pending: w.pending,
        }))
      );
      const empty = emptyLevels();
      if (empty.length) console.warn('[progression] levels with no unlock:', empty);
      console.log('[progression]', s);
      return { ...s, roster: model.weapons.length, emptyLevels: empty };
    },

    /**
     * Point the ladder at a different weapon module and rebuild the screen in place.
     *
     * The roster is derived from a file this feature does not own and which is being
     * rewritten concurrently, so being able to substitute a fixture is the difference
     * between testing this screen and testing whatever today's dataset happens to be.
     * Pass nothing to go back to the real one.
     */
    setWeaponSource: (source) => {
      const n = setWeaponSource(source);
      // `Progression._resolveWeapons` prefers `ctx.weapons.defs` over the module, and
      // on the real `WeaponSystem` that is a getter with no setter. So the context is
      // shimmed for exactly the duration of the resolve rather than mutated — the
      // model reads `defs` and nothing else off it during that call.
      const realCtx = model.ctx;
      if (source) model.ctx = { ...realCtx, weapons: { defs: source.WEAPONS } };
      try {
        model._resolveWeapons?.();
      } finally {
        model.ctx = realCtx;
      }
      model._ensureLoadout?.();
      loadout.rebuild();
      return n;
    },
    curve: () => {
      const rows = [];
      let cum = 0;
      for (let L = 1; L < MODEL.maxLevel; L++) {
        const cost = xpCost(L);
        cum += cost;
        rows.push({ level: L, cost, cumulativeToNext: cum });
      }
      console.table(rows);
      console.log('total to 55:', cum);
      return rows;
    },
    openLoadout: () => loadout.open(),
    closeLoadout: () => loadout.close(),
    /** Open the 性能詳細 sheet on a weapon by id, for screenshots and eyeballing. */
    openSheet: (id) => {
      loadout.open();
      // Closed first: `_openSheet` is a no-op when the sheet is already up, which is
      // right for a keypress and wrong for "show me this other weapon".
      loadout._closeSheet();
      const i = loadout.cells.findIndex((w) => w.id === id);
      if (i >= 0) loadout.cursor = i;
      loadout._render();
      loadout._scrollToCursor();
      loadout._openSheet();
      return loadout.cells[loadout.cursor]?.name ?? null;
    },
    /* ----------------------------- the Gunsmith ---------------------------- */

    /** Open the Gunsmith on any weapon, from anywhere, for screenshots and eyeballing. */
    openGunsmith: (id = model.primary) => {
      loadout.close();
      gunsmith.close();
      return gunsmith.open(id, { fromLoadout: false }) ? id : null;
    },
    closeGunsmith: () => gunsmith.close(),

    /**
     * Put a weapon at a weapon level instantly.
     *
     * Goes through the attachment system, which owns the counter — setting it here
     * would give one number two writers and make this harness measure something the
     * game does not do. It is also the only way the *unlocks* follow the level.
     */
    setWeaponLevel: (n = WEAPON_MAX_LEVEL, id = gunsmith.weaponId ?? model.primary) => {
      const sys = window.__atttest?.loadouts ?? ctx.attachments ?? null;
      sys?.setLevel?.(id, Math.max(1, Math.min(WEAPON_MAX_LEVEL, n)));
      if (gunsmith.isOpen && gunsmith.weaponId === id) {
        gunsmith._rebuildFor(id);
        gunsmith._render();
      }
      return gunsmith.summaryFor(id);
    },
    addWeaponXp: (n = 1000, id = gunsmith.weaponId ?? model.primary) => {
      gunsmith.addWeaponXp(id, n);
      return gunsmith.summaryFor(id);
    },
    /** Fit a part by id through the screen, honouring every rule it and the system have. */
    fit: (attId) => {
      const i = gunsmith.cells?.findIndex?.((a) => a.id === attId) ?? -1;
      if (i < 0) return false;
      gunsmith.cursor = i;
      gunsmith._confirm();
      return gunsmith.state().fittedIds.includes(attId);
    },
    clearFits: () => gunsmith._clearAll(),
    /** Wipe the named build slots. The live build and the career save are elsewhere. */
    resetGunsmith: () => {
      gunsmith.save = { builds: {} };
      gunsmith._flush();
      if (gunsmith.isOpen) gunsmith._render();
    },
    dumpGunsmith: () => {
      const st = gunsmith.isOpen ? gunsmith.state() : null;
      if (st) {
        console.table(
          (gunsmith.catalog ?? []).map((a) => ({
            id: a.id,
            slot: a.slot,
            name: a.name,
            lv: a.unlockLevel,
            cost: a.cost,
            unlocked: a.unlocked,
            fitted: st.fittedIds.includes(a.id),
            pro: a.pro,
            con: a.con,
          }))
        );
      }
      console.log('[gunsmith]', { open: gunsmith.isOpen, weapon: gunsmith.weaponId, state: st, builds: gunsmith.save.builds });
      return st;
    },

    model,
    hud,
    loadout,
    gunsmith,
  };

  return api;
}
